/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import { ReactNode } from 'react';

import { CollapseToggleButton } from '../../../CollapseToggleButton';
import { useStateLocalStorage } from '../../../useHooks';

type CollapsibleSidebarCommonProps = {
  children: (collapseButton: ReactNode) => ReactNode;
  collapsedLabel: string;
  collapseButtonClassName?: string;
  collapseTitle: string;
  expandButtonClassName?: string;
  expandTitle: string;
};

type ControlledCollapsibleSidebarProps = CollapsibleSidebarCommonProps & {
  collapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
  storageKey?: never;
};

type PersistedCollapsibleSidebarProps = CollapsibleSidebarCommonProps & {
  collapsed?: never;
  onCollapsedChange?: never;
  storageKey: string;
};

type CollapsibleSidebarProps = ControlledCollapsibleSidebarProps | PersistedCollapsibleSidebarProps;

function isPersistedSidebar(props: CollapsibleSidebarProps): props is PersistedCollapsibleSidebarProps {
  return typeof props.storageKey === 'string';
}

function CollapsibleSidebarContent({
  children,
  collapsed,
  collapsedLabel,
  collapseButtonClassName,
  collapseTitle,
  expandButtonClassName,
  expandTitle,
  onCollapsedChange,
}: CollapsibleSidebarCommonProps & { collapsed: boolean; onCollapsedChange: (collapsed: boolean) => void }) {
  if (collapsed) {
    return (
      <CollapseToggleButton
        collapsed
        collapsedLabel={collapsedLabel}
        className={expandButtonClassName}
        collapseTitle={collapseTitle}
        expandTitle={expandTitle}
        onClick={() => onCollapsedChange(false)}
      />
    );
  }

  const collapseButton = (
    <CollapseToggleButton
      collapsed={false}
      collapsedLabel={collapsedLabel}
      className={collapseButtonClassName}
      collapseTitle={collapseTitle}
      expandTitle={expandTitle}
      onClick={() => onCollapsedChange(true)}
    />
  );

  return <>{children(collapseButton)}</>;
}

function PersistedCollapsibleSidebar({ storageKey, ...props }: CollapsibleSidebarCommonProps & { storageKey: string }) {
  const [collapsed, setCollapsed] = useStateLocalStorage(storageKey, false);
  return <CollapsibleSidebarContent {...props} collapsed={collapsed} onCollapsedChange={setCollapsed} />;
}

export function CollapsibleSidebar(props: CollapsibleSidebarProps) {
  if (isPersistedSidebar(props)) return <PersistedCollapsibleSidebar {...props} />;

  const { collapsed, onCollapsedChange, ...commonProps } = props;
  return <CollapsibleSidebarContent {...commonProps} collapsed={collapsed} onCollapsedChange={onCollapsedChange} />;
}
